import api from '@api';
import {
    Menu, MenuItem, TextField
} from '@mui/material';
import { Button, Loader } from '@tsp-ui/core/components';
import { useConfirm, usePageMessage, useParams } from '@tsp-ui/core/utils';
import { useGetCurrentAccount } from '@utils/hooks';
import { useContext, useRef, useState } from 'react';

import styles from './LockAvailabilityButton.module.scss';
import { LockAvailabilityContext } from './LockAvailabilityPage';


export default function LockAvailabilityButton() {
    const [ suspendLoading, setSuspendLoading ] = useState(false);
    const [ anchorEl, setAnchorEl ] = useState<HTMLElement>();
    const commentsRef = useRef<HTMLInputElement>(null);

    const confirm = useConfirm();
    const pageMessage = usePageMessage();
    const { customerID } = useParams();
    const { id: clientID } = useGetCurrentAccount();

    const { lockSuspension, setLockSuspension } = useContext(LockAvailabilityContext);

    return (
        <>
            <Button
                variant="contained"
                onClick={event => setAnchorEl(event.currentTarget)}
                disabled={lockSuspension?.isSuspended || lockSuspension?.isSuspendedUntilNewRateSheet}
                tooltip={(lockSuspension?.isSuspended || lockSuspension?.isSuspendedUntilNewRateSheet) ? 'Cannot suspend locks when they are already suspended' : ''}
            >
                Suspend locks
            </Button>

            <Menu
                open={!!anchorEl}
                onClose={() => setAnchorEl(undefined)}
                anchorEl={anchorEl}
                anchorOrigin={{
                    horizontal: 'right',
                    vertical: 'bottom'
                }}
                transformOrigin={{
                    horizontal: 'right',
                    vertical: 'top'
                }}
            >
                <MenuItem
                    disabled={lockSuspension?.isSuspended}
                    onClick={async () => {
                        setSuspendLoading(true);

                        if (await confirm(
                            <>
                                Are you sure you want to suspend all locks?

                                <TextField
                                    className={styles.comments}
                                    label="Additional comments"
                                    inputRef={commentsRef}
                                    defaultValue={lockSuspension?.suspendedComments}
                                />
                            </>
                        )) {
                            try {
                                setLockSuspension(
                                    await api.availability.createLockSuspension(clientID, {
                                        customerId: customerID || null,
                                        isSuspended: true,
                                        isSuspendedUntilNewRateSheet: false,
                                        suspendedComments: commentsRef.current?.value || null
                                    }, customerID)
                                );

                                pageMessage.success('Locks supended');
                            } catch (error) {
                                pageMessage.handleApiError('An error occurred while suspending locks', error);
                            }
                        }

                        setSuspendLoading(false);
                        setAnchorEl(undefined);
                    }}
                >
                    Suspend all locks
                </MenuItem>

                <MenuItem
                    disabled={lockSuspension?.isSuspendedUntilNewRateSheet}
                    onClick={async () => {
                        setSuspendLoading(true);

                        if (await confirm(
                            <>
                                Are you sure you want to suspend locks until new ratesheet?

                                <TextField
                                    className={styles.comments}
                                    label="Additional comments"
                                    inputRef={commentsRef}
                                    defaultValue={lockSuspension?.suspendedComments}
                                />
                            </>
                        )) {
                            try {
                                setLockSuspension(
                                    await api.availability.createLockSuspension(clientID, {
                                        customerId: customerID || null,
                                        isSuspended: false,
                                        isSuspendedUntilNewRateSheet: true,
                                        suspendedComments: commentsRef.current?.value || null
                                    }, customerID)
                                );

                                pageMessage.success('Locks suspended until new ratesheet');
                            } catch (error) {
                                pageMessage.handleApiError(
                                    'An error occurred while suspending locks until new ratesheet', error
                                );
                            }
                        }

                        setSuspendLoading(false);
                        setAnchorEl(undefined);
                    }}
                >
                    Suspend until new ratesheet
                </MenuItem>
            </Menu>

            <Loader loading={suspendLoading} />
        </>
    );
}
